import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { ServiceListSettings } from '../../../legacy/appSettings/appSettings';
import {
  BookingsQueryParams,
  getUrlQueryParamValue,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { DisplayServicesByOptions } from '../../types/types';
import { FilterType, SelectedResources } from '../../../legacy/types';
import {
  SINGLE_SERVICE_EDITOR_X_PRESET_ID,
  SINGLE_SERVICE_PRESET_ID,
} from '../../consts';
import { getPresetId } from '../../components/BookOnline/controllerPrePageReady';
import settingsParams from '../../components/BookOnline/settingsParams';
import { QueryServicesFilter } from '../../api/BookingsApi';

export const buildQueryServicesFilter = ({
  flowAPI,
  shouldWorkWithAppSettings,
  appSettings,
}: {
  shouldWorkWithAppSettings: boolean;
  appSettings?: ServiceListSettings;
  flowAPI: ControllerFlowAPI;
}): QueryServicesFilter => {
  const {
    controllerConfig: { wixCodeApi, config },
    environment: { isEditorX },
    settings,
  } = flowAPI;

  const filter: QueryServicesFilter = {};
  const preSelectedResource = getUrlQueryParamValue(
    wixCodeApi,
    BookingsQueryParams.RESOURCE,
  );

  if (preSelectedResource) {
    filter.staffMemberIds = [preSelectedResource];
  }

  const returnArrayIfLengthExists = <T extends any[]>(arr: T) =>
    arr.length > 0 ? arr : undefined;

  if (shouldWorkWithAppSettings) {
    const filterBy = appSettings?.FILTER_BY;
    if (filterBy === DisplayServicesByOptions.BY_LOCATIONS) {
      const selectedLocations = appSettings?.SELECTED_LOCATIONS as string[];
      filter.locationIds = returnArrayIfLengthExists(selectedLocations);
    } else {
      const selectedResources =
        appSettings?.SELECTED_RESOURCES as SelectedResources;
      if (!selectedResources) {
        return filter;
      }

      const { categories, filter: filterType, offerings } = selectedResources;

      if (filterType === FilterType.ALL) {
        return filter;
      }

      if (filterType === FilterType.FIRST) {
        filter.limit = 1;
        return filter;
      }

      filter.categoryIds = returnArrayIfLengthExists(categories);
      filter.serviceIds = returnArrayIfLengthExists(offerings);
    }

    return filter;
  }

  if (
    [SINGLE_SERVICE_PRESET_ID, SINGLE_SERVICE_EDITOR_X_PRESET_ID].includes(
      getPresetId(config, isEditorX),
    )
  ) {
    const selectedService = settings.get(settingsParams.selectedService);
    if (selectedService) {
      filter.serviceIds = [selectedService];
    } else {
      filter.limit = 1;
    }

    return filter;
  }

  const displayServicesBy = settings.get(settingsParams.displayServicesBy);

  if (displayServicesBy === DisplayServicesByOptions.BY_SERVICES) {
    const selectedCategories = settings.get(settingsParams.selectedCategories);

    filter.categoryIds = returnArrayIfLengthExists(selectedCategories);
  } else if (
    displayServicesBy === DisplayServicesByOptions.BY_SPECIFIC_SERVICES
  ) {
    const selectedServices = settings.get(settingsParams.selectedServices);

    filter.serviceIds = returnArrayIfLengthExists(selectedServices);
  } else if (displayServicesBy === DisplayServicesByOptions.BY_LOCATIONS) {
    const selectedLocations = settings.get(settingsParams.selectedLocations);
    filter.locationIds = returnArrayIfLengthExists(selectedLocations);
  }

  if (flowAPI.experiments.enabled('specs.bookings.fetchTabsInServiceList')) {
    const preSelectedCategory = getUrlQueryParamValue(
      wixCodeApi,
      BookingsQueryParams.CATEGORY,
    );

    if (preSelectedCategory) {
      filter.categoryIds = [preSelectedCategory];
    }

    const preSelectedLocation = getUrlQueryParamValue(
      wixCodeApi,
      BookingsQueryParams.LOCATION,
    );

    if (preSelectedLocation) {
      filter.locationIds = [preSelectedLocation];
    }
  }

  return filter;
};
